import { useQuery } from "@tanstack/react-query";
import { getUsersDetails } from "../hooks/fetching/getUserDetails";
import { useAuth } from "../context/AuthContext";
import { Bell, CheckCircle, ChevronRight, ClipboardListIcon, Clock, Dot, Play, Star, User } from "lucide-react";
import { Progress } from "@gravity-ui/uikit";
import SelectLabeledText from "../components/LabelSelect";
import { Link, useNavigate } from "react-router-dom";
import { getUserSurveyList } from "../hooks/fetching/getUserSurveys";
import { ButtonComponent } from "../components/ui/buttonComponent";

const UserHome = () => {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const { data, isFetching } = useQuery({
    queryKey: ["userDetails", userId],
    queryFn: () => getUsersDetails(userId || "", true),
    retry: 2,
    staleTime: 120 * 60_000,
  });

  const { data: surveys, isFetching: isFetchingSurveys } = useQuery({
    queryKey: ["surveys"],
    queryFn: () => getUserSurveyList(),
    retry: 2,
  });

  return (
    <>
      {isFetching ? (
        <div className="h-[220px] flex justify-center items-center">
          <div className="animate-spin rounded-full self-center h-10 w-10 md:h-12 md:w-12 border-b-2 border-orange-500" />
        </div>
      ) : (
        <div className="w-full h-full flex flex-col gap-6 md:gap-8 animate-in fade-in slide-in-from-bottom-8 duration-400 ease-out p-4 md:p-6">
          {/* Welcome */}

          <div className="flex flex-col gap-3">
            {(surveys?.surveys ?? [])?.map((survey) => (
              <div
                key={survey?.id}
                className="bg-xuron-orange rounded-2xl w-full flex flex-col md:flex-row items-start md:items-center p-4 md:p-6 justify-between gap-4 md:gap-0"
              >
                {/* Left side */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500 shrink-0 flex items-center rounded-full justify-center">
                    <Bell className="text-white h-5 w-5 shrink-0" />
                  </div>
                  <p className="text-base md:text-lg font-bold text-white">You have a new survey!</p>
                </div>

                {/* Right side */}
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 w-full md:w-auto">
                  <div className="px-3 py-0.5 rounded-2xl bg-white text-xuron-orange text-xs md:text-sm font-semibold self-start md:self-center">
                    {survey?.courseName}
                  </div>
                  <div className="hidden md:block">
                    <Dot className="text-white opacity-70" />
                  </div>
                  <p className="text-xs md:text-sm font-medium text-white">
                    {new Date(survey?.sent_on || "").toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                  <ButtonComponent
                    onClick={() =>
                      navigate(`/session/survey?id=${survey?.session_id}&survey_id=${survey?.id}&backto=/home`)
                    }
                    className="mt-2 md:mt-0 flex items-center gap-1 !bg-white !text-xuron-orange w-full md:w-auto justify-center"
                  >
                    <ClipboardListIcon />
                    Take Survey
                  </ButtonComponent>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-4 md:gap-6">
            <div className="flex flex-col gap-1">
              <h1 className="font-bold text-2xl md:text-3xl text-gray-900">
                Welcome back, {data?.first_name} {data?.last_name ?? ""}!
              </h1>
              <h2 className="text-gray-500 font-medium text-sm md:text-base">
                Track your learning progress and review your completed simulation sessions
              </h2>
            </div>

            {/* Stats Cards */}
            <div className="flex flex-wrap gap-4 justify-around">
              {/* Total Sessions */}
              <div className="p-4 md:p-6 bg-white flex w-full sm:w-[48%] lg:w-[220px] shadow border border-gray-200 rounded-2xl">
                <div className="flex flex-col gap-1 md:gap-2">
                  <p className="font-semibold text-xs md:text-sm text-gray-600">Total Sessions</p>
                  <h1 className="font-bold text-2xl md:text-4xl text-gray-900">{data?.stats?.total_sessions ?? 0}</h1>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 shrink-0 ml-auto my-auto bg-xuron-orange rounded-full flex justify-center items-center">
                  <Play className="w-5 h-5 md:w-6 md:h-6 text-white shrink-0" />
                </div>
              </div>

              {/* Completed Sessions */}
              <div className="p-4 md:p-6 bg-white flex w-full sm:w-[48%] lg:w-[220px] shadow border border-gray-200 rounded-2xl">
                <div className="flex flex-col gap-1 md:gap-2">
                  <p className="font-semibold text-xs md:text-sm text-gray-600">Completed Sessions</p>
                  <h1 className="font-bold text-2xl md:text-4xl text-green-600">
                    {data?.stats?.completed_sessions ?? 0}/{data?.stats?.total_sessions ?? 0}
                  </h1>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 shrink-0 ml-auto my-auto bg-xuron-orange rounded-full flex justify-center items-center">
                  <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-white shrink-0" />
                </div>
              </div>

              {/* Total Time */}
              <div className="p-4 md:p-6 bg-white flex w-full sm:w-[48%] lg:w-[220px] shadow border border-gray-200 rounded-2xl">
                <div className="flex flex-col gap-1 md:gap-2">
                  <p className="font-semibold text-xs md:text-sm text-gray-600">Total Time</p>
                  <h1 className="font-bold text-2xl md:text-4xl text-blue-600">{data?.stats?.total_time_human ?? 0}</h1>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 shrink-0 ml-auto my-auto bg-xuron-orange rounded-full flex justify-center items-center">
                  <Clock className="w-5 h-5 md:w-6 md:h-6 text-white shrink-0" />
                </div>
              </div>

              {/* Avg Feedback */}
              <div className="p-4 md:p-6 bg-white flex w-full sm:w-[48%] lg:w-[220px] shadow border border-gray-200 rounded-2xl">
                <div className="flex flex-col gap-1 md:gap-2">
                  <p className="font-semibold text-xs md:text-sm text-gray-600">Avg Feedback</p>
                  <h1 className="font-bold text-2xl md:text-4xl text-purple-600">
                    {data?.stats?.avg_feedback ?? 0}/5.0
                  </h1>
                </div>
                <div className="w-10 h-10 md:w-12 md:h-12 shrink-0 ml-auto my-auto bg-xuron-orange rounded-full flex justify-center items-center">
                  <Star className="w-5 h-5 md:w-6 md:h-6 text-white shrink-0" />
                </div>
              </div>
            </div>
          </div>

          {/* Sessions */}
          <div className="flex flex-col gap-4 md:gap-6">
            <h1 className="font-bold text-xl md:text-2xl text-gray-900">Your Learning Sessions</h1>

            {(data?.sessions || []).length > 0 ? (
              <div className="shadow p-4 rounded-2xl bg-white flex flex-col gap-4">
                {data?.sessions?.map((session) => (
                  <div
                    key={session?.session_id}
                    className="flex flex-col bg-white md:flex-row md:items-center gap-3 w-full border border-gray-200 rounded-2xl p-4 md:p-5"
                  >
                    <div className="flex flex-col gap-4 w-full">
                      <div className="flex gap-3 md:gap-4 items-center">
                        <div className="w-12 h-12 md:w-14 md:h-14 shrink-0 bg-xuron-orange rounded-full flex items-center justify-center">
                          <User className="text-white h-6 w-6 md:h-8 md:w-8" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <h1 className="font-bold text-sm md:text-base text-gray-900">{session?.courseName}</h1>
                          <div className="flex flex-col">
                            <h2 className="font-bold text-xs md:text-sm text-gray-900">
                              {new Date(session?.createdAt).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })}
                            </h2>
                            <p className="text-xs md:text-sm font-bold text-gray-500">
                              {new Date(session?.createdAt).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Progress className="w-full my-progress !rounded-full overflow-hidden" value={100} size="s" />
                        <div className="flex flex-wrap gap-2 items-center">
                          <SelectLabeledText label={session?.completed} />
                          <div className="ml-auto flex gap-2 md:gap-3">
                            <div className="bg-red-100 px-2 py-1 rounded-lg text-red-500 font-semibold text-xs md:text-sm">
                              PRE: {session?.pretest?.display}
                            </div>
                            <div className="bg-green-100 px-2 py-1 rounded-lg text-green-500 font-semibold text-xs md:text-sm">
                              POST: {session?.posttest?.display}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Link
                      to={`/session/?id=${session?.session_id}&user_id=${userId}&backto=/home`}
                      className="self-end md:self-auto"
                    >
                      <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No sessions available yet.</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default UserHome;
